import type { ComponentProps } from 'react';

import { headingVariants } from '@vs-shop/ui/heading';
import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * A band of a product page's lower content: a title over a block of whatever
 * that band is about — the long description, the parameters, the accessories.
 * Flat named parts a tenant arranges in its own JSX, each painting itself.
 *
 *   <ProductBand id={anchor}>
 *     <ProductBandStack>
 *       <ProductBandTitle render={<Heading level={2} />}>{title}</ProductBandTitle>
 *       {body}
 *     </ProductBandStack>
 *
 *     <ProductBandDisclosure open={isOpen} onOpenChange={setOpen}>
 *       <ProductBandDisclosureTrigger ref={triggerRef}>
 *         <ProductBandDisclosureHeader>
 *           <ProductBandTitle render={<Heading level={2} />}>{title}</ProductBandTitle>
 *           <ProductBandDisclosureChevron />
 *         </ProductBandDisclosureHeader>
 *       </ProductBandDisclosureTrigger>
 *       <ProductBandDisclosureContent>{body}</ProductBandDisclosureContent>
 *     </ProductBandDisclosure>
 *   </ProductBand>
 *
 * **The band is rendered twice and shown once.** A page with room stands the
 * whole band open; a phone folds it behind its own title. Both are in the
 * markup — the same body composed into each — and the breakpoint decides which
 * is seen, which is why the anatomy is two siblings rather than one part with
 * an appearance axis. The parts common to both, the title above all, are
 * composed into each rather than duplicated per placement.
 *
 * **That step is the section's, and it is in these recipes** — {@link
 * ProductBandStack} appears from `lap` up and the disclosure below it. Neither
 * part takes an axis, so a tenant that wants a band open at every width, or
 * folded at every width, overrides the one display utility through `className`
 * rather than composing the step itself: leaving it to a composition would put
 * both halves of every band on the page at once the first time someone forgot.
 *
 * Nothing here decides what a band is about. Its title, its body, and the
 * anchor a strip jumps to are the tenant's; the parts take copy as children and
 * the `id` through the native prop spread, so no shared part owns a policy over
 * either. A description that arrives as markup stays the tenant's to render,
 * through `@vs-shop/ui/markdown`, which sanitises it — nothing in this file
 * interprets a string.
 *
 * Server-first: every part in this file is a plain server component. The state
 * a folded band runs on lives in {@link useProductBandDisclosure}, which a
 * tenant calls from its own client composition.
 */

export type ProductBandProps = ComponentProps<'div'>;

/**
 * The band itself.
 *
 * Carries no recipe: where a band sits, and what it sits beside, is page
 * assembly — the product page runs the description and the parameters as two
 * columns of one grid, and a leaner page would stack them. What this
 * contributes is the element an anchor strip's link lands on, and the one the
 * anatomy is reachable by.
 */
export const ProductBand = ({ className, ...props }: ProductBandProps) => (
  <div data-slot="product-band" className={optionalClassName(clsxm(className))} {...props} />
);

/**
 * The band standing open, from the width a page has room for it.
 *
 * The step between the title and the body is the stack's own, and it does not
 * depend on how many children arrive: a band is correct with a body alone and
 * with a title over one.
 */
const productBandStackClasses = 'lap:flex hidden flex-col gap-8';

export type ProductBandStackProps = ComponentProps<'div'>;

/** The open band. */
export const ProductBandStack = ({ className, ...props }: ProductBandStackProps) => (
  <div
    data-slot="product-band-stack"
    className={clsxm(productBandStackClasses, className)}
    {...props}
  />
);

/**
 * What the band is called.
 *
 * The scale is taken from `@vs-shop/ui`'s heading primitive at its second level
 * rather than restated here, so a page supplying `render={<Heading level={2} />}`
 * renders one scale rather than two fighting over the same element.
 */
const productBandTitleClasses = headingVariants({ level: 2 });

export type ProductBandTitleProps = ComponentProps<'div'> & {
  /**
   * The element to paint instead of the default `<div>` — the one place a page
   * decides whether this text is a heading, and at what level.
   *
   * A product page runs several of these bands under the product's own name, so
   * on that page each is an `<h2>`; the same band inside a drawer, a comparison
   * column or a tab panel must not contribute a heading to whatever placed it.
   * A band that renders its title twice — open and folded — hands the same
   * element to both, so one page contributes one outline entry however wide the
   * viewport is.
   *
   * What it does NOT decide is the type scale: the recipe above wins over the
   * heading primitive's own, so `render={<Heading level={3} />}` renders an
   * `<h3>` at a band title's size. That is the point of taking the scale from
   * one place — a page moving a band down its outline should not shrink the
   * band — and a page that wants the primitive's scale instead says so with
   * `className`.
   *
   * Declared by what this part reads off the element rather than by a tag, so
   * any element with a `className` qualifies.
   */
  readonly render?: RenderElement;
};

/** The band's title. Carries the type scale; the element is the page's to choose. */
export const ProductBandTitle = ({ className, render, ...props }: ProductBandTitleProps) => (
  <RenderedPart
    dataSlot="product-band-title"
    className={clsxm(productBandTitleClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * What a band says when it has nothing to say — no description written yet, no
 * parameters recorded against the product.
 *
 * A part rather than a tenant's own line, because an empty band is a state the
 * band has rather than content a page supplies: the muting is what keeps a
 * shopper from reading "not available" as the product's own copy. What it SAYS
 * is still the tenant's, and arrives as children.
 */
const productBandNoteClasses = 'text-foreground-muted';

export type ProductBandNoteProps = ComponentProps<'div'>;

/** The band's empty state. */
export const ProductBandNote = ({ className, ...props }: ProductBandNoteProps) => (
  <div
    data-slot="product-band-note"
    className={clsxm(productBandNoteClasses, className)}
    {...props}
  />
);
